import { PayChannel, type WalletSign } from "@api/memberApi";
import { wrapperResult } from "@api/osp";

import log from "logbeacon";
import { useContext } from "react";
import { getDeviceType, getGaInfoForReporting } from "utils";
import { mergeAdjustAdidIntoHeaders } from "@/src/lib/bridge/adjustRequestHeaders";
import { OspApiClientContext } from "@/src/features";

export const usePayment = () => {
  const { memberApi } = useContext(OspApiClientContext);

  const handlePaymentCheck = async (planId: string) => {
    try {
      if (!memberApi?.payment?.paymentCheck) {
        throw new Error("Payment check client is not available");
      }
      const gaInfo = await getGaInfoForReporting();
      const headers = mergeAdjustAdidIntoHeaders({
        "ga-instance-id": gaInfo.gaInstanceId || "",
        "ga-client-id": gaInfo.clientId || "",
        "ga-session-id": gaInfo.sessionId || "",
        "os-device-type": getDeviceType(),
      });
      const res = await memberApi.payment.paymentCheck(
        {
          product_id: planId,
          pay_channel: PayChannel.STRIPE,
          payment_context: {
            return_url: `${window.location.origin}${window.location.pathname}?payment=success&planId=${planId}`,
            cancel_url: `${window.location.origin}${window.location.pathname}?payment=cancel&planId=${planId}`,
          },
        },
        {
          headers,
        },
      );
      return {
        data: res.data?.data?.offer_details?.[0]?.links?.[0]?.href,
        error: null,
      };
    } catch (error: unknown) {
      const err = error as { code?: string; msg?: string; data?: unknown };
      log.error("[getPaymentCheck error]", error, planId);
      return {
        data: null,
        error: {
          code: err.code,
          msg: err.msg,
          data: err.data,
        },
      };
    }
  };

  const handleWalletSign = async ({
    planId,
    eoa_address,
    chain_id,
    token_address,
    sign_type,
    type,
  }: {
    planId?: string;
    eoa_address: string;
    chain_id: number;
    token_address?: string;
    sign_type?: string;
    type?: string;
  }) => {
    try {
      if (!memberApi?.wallet?.sign) {
        throw new Error("Wallet sign client is not available");
      }
      const gaInfo = await getGaInfoForReporting();
      const headers = mergeAdjustAdidIntoHeaders({
        "ga-instance-id": gaInfo.gaInstanceId || "",
        "ga-client-id": gaInfo.clientId || "",
        "ga-session-id": gaInfo.sessionId || "",
        "os-device-type": getDeviceType(),
      });
      const res = await wrapperResult<WalletSign>(
        memberApi.wallet.sign(
          {
            eoa_address,
            product_id: planId,
            chain_id,
            token_address,
            type: type || "EDGEN_SUB",
            sign_type: sign_type || "PAYMENT",
          },
          {
            headers,
          },
        ),
      );
      log.debug("[buildPaymentTxData]", res, planId);
      return {
        data: res,
        error: null,
      };
    } catch (error: unknown) {
      const err = error as {
        data?: { code?: string; msg?: string; data?: unknown };
      };
      log.error("[walletSign error]", err?.data, err?.data?.code, planId);
      return {
        data: null,
        error: {
          code: err.data?.code,
          msg: err.data?.msg,
          data: err.data?.data,
        },
      };
    }
  };

  return {
    handleWalletSign,
    handlePaymentCheck,
  };
};
